/**
 * V10 Phase C — 공통 Confirm Modal (카운트다운 + 선택적 reason 필수).
 *
 * Edge Inspector 의 3종 무력화 (link / episode / edge) 가 모두 이 컴포넌트를 재사용.
 * SuspendModal 과 별개 — SuspendModal 은 카운트다운이 없다.
 *
 * **Props 차등화**:
 * - `countdown: 2` → 2초 카운트다운 (Link 단위 무력화, 상대적으로 덜 위험)
 * - `countdown: 3` → 3초 카운트다운 + `requireReason: true` (Episode 본체 / Edge 전체)
 * - `tone`: "danger" / "warning" — 시각적 차등화
 *
 * 접근성:
 * - aria-labelledby="confirm-title"
 * - aria-describedby="confirm-body"
 * - Escape 로 닫힘 (onClose)
 * - `prefers-reduced-motion` 감지 시 fade 애니메이션 제거
 */
import type { ReactNode } from "react"
import { useEffect, useState } from "react"

export type ConfirmCountdownTone = "danger" | "warning"

export type ConfirmCountdownModalProps = {
	readonly title: string
	/**
	 * 스크린리더용 요약 + fallback 텍스트. `bodySlot` 이 주어지면 시각 렌더는 생략하고
	 * aria-describedby 만 위해 off-screen 에 남겨둔다 (접근성 유지).
	 */
	readonly bodyCopy: string
	/** 선택 — 시각적 본문을 JSX 로 구성하고 싶을 때 사용. 있으면 `bodyCopy` 대신 렌더된다. */
	readonly bodySlot?: ReactNode
	readonly countdown: 2 | 3
	readonly requireReason: boolean
	readonly confirmLabel: string
	readonly tone: ConfirmCountdownTone
	readonly onClose: () => void
	readonly onConfirm: (reason?: string) => Promise<void> | void
}

export function ConfirmCountdownModal(props: ConfirmCountdownModalProps) {
	const [remaining, setRemaining] = useState<number>(props.countdown)
	const [reason, setReason] = useState<string>("")
	const [submitting, setSubmitting] = useState(false)
	const [error, setError] = useState<string | undefined>()

	useEffect(() => {
		if (remaining <= 0) return
		const id = setTimeout(() => setRemaining((n) => n - 1), 1000)
		return () => clearTimeout(id)
	}, [remaining])

	useEffect(() => {
		function onKey(e: KeyboardEvent) {
			if (e.key === "Escape") props.onClose()
		}
		window.addEventListener("keydown", onKey)
		return () => window.removeEventListener("keydown", onKey)
	}, [props])

	const canConfirm = remaining <= 0 && !submitting && (!props.requireReason || reason.trim().length > 0)

	async function onSubmit() {
		if (!canConfirm) return
		setSubmitting(true)
		setError(undefined)
		try {
			await props.onConfirm(props.requireReason ? reason.trim() : reason.trim() || undefined)
			props.onClose()
		} catch (e) {
			setError((e as Error).message)
			setSubmitting(false)
		}
	}

	const toneBg = props.tone === "danger" ? "var(--pk-danger-soft)" : "var(--pk-warning-soft)"
	const toneBorder = props.tone === "danger" ? "var(--pk-danger-border)" : "var(--pk-warning-border)"
	const toneBtn = props.tone === "danger" ? "var(--pk-danger)" : "var(--pk-warning)"

	return (
		// biome-ignore lint/a11y/useKeyWithClickEvents: 백드롭 클릭만 닫힘 허용 — Escape 는 useEffect 로 별도 처리
		<div
			className="modal-backdrop"
			role="dialog"
			aria-modal="true"
			aria-labelledby="confirm-title"
			aria-describedby="confirm-body"
			onClick={(e) => {
				if (e.target === e.currentTarget) props.onClose()
			}}
			style={{
				position: "fixed",
				inset: 0,
				background: "rgba(0,0,0,0.4)",
				display: "flex",
				alignItems: "center",
				justifyContent: "center",
				zIndex: 1000
			}}
		>
			<div
				style={{
					width: "min(420px, 92vw)",
					background: "var(--pk-bg-elevated)",
					border: `1px solid ${toneBorder}`,
					borderRadius: "var(--pk-radius-lg)",
					padding: "var(--pk-space-6)",
					boxShadow: "var(--pk-shadow-lg)"
				}}
			>
				<h3 id="confirm-title" style={{ margin: 0, marginBottom: "var(--pk-space-3)" }}>
					{props.title}
				</h3>
				{props.bodySlot ? (
					<div id="confirm-body" style={{ marginBottom: "var(--pk-space-4)" }}>
						{props.bodySlot}
						<span
							style={{
								position: "absolute",
								width: 1,
								height: 1,
								padding: 0,
								margin: -1,
								overflow: "hidden",
								clip: "rect(0,0,0,0)",
								whiteSpace: "nowrap",
								border: 0
							}}
						>
							{props.bodyCopy}
						</span>
					</div>
				) : (
					<p
						id="confirm-body"
						style={{
							background: toneBg,
							border: `1px solid ${toneBorder}`,
							borderRadius: "var(--pk-radius-md)",
							padding: "var(--pk-space-3)",
							fontSize: "0.9rem",
							margin: 0,
							marginBottom: "var(--pk-space-4)",
							color: "var(--pk-text-primary)"
						}}
					>
						{props.bodyCopy}
					</p>
				)}

				{props.requireReason && (
					<div className="field" style={{ marginBottom: "var(--pk-space-4)" }}>
						<label
							className="field-label"
							htmlFor="confirm-reason"
							style={{ display: "block", marginBottom: "var(--pk-space-1)" }}
						>
							사유 (필수)
						</label>
						<textarea
							id="confirm-reason"
							className="input"
							value={reason}
							onChange={(e) => setReason(e.target.value)}
							placeholder="예: 책 개정판에서 해당 scene 삭제"
							rows={3}
							style={{ width: "100%" }}
						/>
					</div>
				)}

				{error && (
					<div className="alert alert-danger" style={{ marginBottom: "var(--pk-space-3)" }}>
						{error}
					</div>
				)}

				<div
					style={{
						display: "flex",
						gap: "var(--pk-space-2)",
						justifyContent: "flex-end"
					}}
				>
					<button type="button" className="btn btn-ghost" onClick={props.onClose} disabled={submitting}>
						취소
					</button>
					<button
						type="button"
						className="btn"
						onClick={onSubmit}
						disabled={!canConfirm}
						style={{
							background: canConfirm ? toneBtn : "var(--pk-gray-300)",
							color: "#fff",
							border: "none"
						}}
					>
						{remaining > 0
							? `${props.confirmLabel} (${remaining})`
							: submitting
								? "처리 중…"
								: props.confirmLabel}
					</button>
				</div>
			</div>
		</div>
	)
}
